import React, {useEffect, useState} from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Upload,
    message,
    Layout,
    Space,
    Select,
    List,
    Tag,
    Image, Popconfirm, Avatar, DatePicker, Row, Alert, Popover, Tabs, InputNumber, Col
} from 'antd';
import {
    UploadOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    UserAddOutlined,
    UserOutlined
} from '@ant-design/icons';
import Api from "../api";
import dayjs from "dayjs";
import {Player} from '@lottiefiles/react-lottie-player';
import { StarOutlined, SmileOutlined, PictureOutlined } from '@ant-design/icons';

const {Content} = Layout;

export function LottieOrImageRenderer({image_url}) {
    const fullSize = {width: 160, height: 160};

    if (image_url.endsWith('.json')) {
        return (
            <Popover content={<Player autoplay loop style={fullSize} src={image_url}/>} trigger="hover"
                     overlayStyle={{padding: 0}}>
                <div>
                    <Player autoplay loop style={{width: 40, height: 40, cursor: 'pointer', margin: 0}}
                            src={image_url}/>
                </div>
            </Popover>
        );
    } else {
        return (
            <Image src={image_url} alt="icon"
                   style={{width: 40, height: 40}}/>
        )
    }
}

const BadgesView = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingBadge, setEditingBadge] = useState(null);
    const [form] = Form.useForm();
    const [userModalVisible, setUserModalVisible] = useState(false);
    const [selectedBadge, setSelectedBadge] = useState(null);
    const [activeTab, setActiveTab] = useState('badge');
    const [fileList, setFileList] = useState([]);
    const [badges, setBadges] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    const loadDecorations = (page = 1, type = activeTab) => {
        Api.get(`/admin/decorations?page=${page}&type=${type}`)
            .then((res) => {
                setBadges(res.data.data);

                setPagination({
                    current: res.data.meta.current_page,
                    pageSize: res.data.meta.per_page,
                    total: res.data.meta.total,
                });
            });
    };

    useEffect(() => {
        loadDecorations(1, activeTab);
    }, [activeTab]);
    const showModal = (badge = null) => {
        setEditingBadge(badge);

        if (badge) {
            const existingImgUrl = badge.image_url ? badge.image_url.replace('v2.fitlap.ee', 'fitlap.in') : '';

            // Show preview
            const previewFile = {
                uid: '-1',
                name: '',
                status: 'done',
                url: existingImgUrl,
            };

            // Update form + fileList
            form.setFieldsValue({
                name: badge.name,
                type: badge.type,
                description: badge.description,
                progress_name: badge.progress_name,
                total_count: badge.total_count,
                metadata: badge.metadata,
                image: existingImgUrl, // only set base64 or URL in form, not array
            });

            setFileList([previewFile]);
        } else {
            setFileList([]);
            form.resetFields();
        }

        setIsModalVisible(true);
    };

    const handleOk = () => {
        form.validateFields().then(async (values) => {
            const {name, type, date, description, total_count, progress_name, metadata} = values;

            const fileItem = fileList[0];
            let imageFile;

            // Only require image on create
            if (!fileItem || !fileItem.url) {
                if (!editingBadge) {
                    message.error('No image uploaded');
                    return;
                }
            }

            // New image uploaded as Base64
            if (fileItem && fileItem.url.startsWith('data:')) {
                const res = await fetch(fileItem.url);
                const blob = await res.blob();
                imageFile = new File([blob], fileItem.name || 'badge', {type: blob.type});
            } else if (fileItem?.originFileObj) {
                imageFile = fileItem.originFileObj;
            } else if (!editingBadge) {
                // On create, image is required
                message.error("Please upload a new image.");
                return;
            }

            const formData = new FormData();

            formData.append('name', name);
            formData.append('type', type);

            let parsedMetadata = metadata;

            try {
                parsedMetadata = JSON.stringify(JSON.parse(metadata));
            } catch (e) {
                message.error("Metadata must be valid JSON");
                return;
            }

            formData.append('metadata', parsedMetadata);

            if (description) {
                formData.append('description', description);
            }

            formData.append('progress_name', progress_name ? progress_name : '');

            formData.append('total_count', total_count ? total_count : 0);

            if (date) {
                formData.append('expires_at', dayjs(date).format('YYYY-MM-DD'));
            }

            if (imageFile) {
                formData.append('image', imageFile); // real image file now
            }

            Api.post('/admin/decorations' + (editingBadge ? "/" + editingBadge.id : ""), formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            }).then(res => {
                message.success('Badge saved!');
                setIsModalVisible(false);
                form.resetFields();
                setFileList([]);
                setEditingBadge(null);

                // Reload list
                loadDecorations(pagination.current, activeTab);
            }).catch(() => {
                message.error('Upload failed');
            });
        });
    };

    const handleDelete = (id) => {
        Api.delete(`/admin/decorations/${id}`).then(res => {
            message.success("Deleted!");

            // Reload list
            loadDecorations(pagination.current, activeTab);
        })
    }

    const handleAddUsers = (badge) => {
        setSelectedBadge(badge);
        setUserModalVisible(true);
    }

    const columns = [
        {title: 'ID', dataIndex: 'id'},
        {
            title: 'Icon',
            dataIndex: 'image_url',
            render: (image_url) => <LottieOrImageRenderer image_url={image_url}/>,
        },
        {
            title: 'Title',
            dataIndex: 'name',
        },
        {
            title: 'Description',
            dataIndex: 'description',
        },
        {
            title: 'Progress',
            dataIndex: 'progress_name',
            render: (text, record) => (
                text ? <div><Tag>{text}</Tag> <Tag>{record.total_count}</Tag></div> : ''
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            render: (text) => (
                text ? <Tag>{text}</Tag> : ''
            ),
        },
        {
            title: 'Unlocked',
            dataIndex: 'unlocked_count',
        },
        {
            title: 'Active',
            dataIndex: 'active_count',
        },
        {
            width: 160,
            title: 'Action',
            render: (_, record) => (
                <Space>
                    <Button icon={<UserAddOutlined/>} onClick={() => handleAddUsers(record)}>Users</Button>
                    <Button icon={<EditOutlined/>} onClick={() => showModal(record)}>Edit</Button>

                    <Popconfirm
                        title="Delete the badge"
                        description="Are you sure to delete this badge?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >

                        <Button icon={<DeleteOutlined/>}>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Content style={{padding: "20px"}}>
            <h1>Decorations</h1>

            <Button
                type="primary"
                icon={<PlusOutlined/>}
                onClick={() => showModal()}
                style={{marginBottom: 16}}
            >
                New Decoration
            </Button>

            <Tabs
                activeKey={activeTab}
                items={[
                    { key: 'badge', label: 'Badges' },
                    { key: 'sticker', label: 'Stickers' },
                    { key: 'banner', label: 'Banners' },
                ]}
                onChange={(key) => {
                    setActiveTab(key);
                    setPagination(prev => ({
                        ...prev,
                        current: 1,
                    }));
                }}
            />

            <Table
                dataSource={badges}
                columns={columns}
                rowKey="id"
                pagination={pagination}
                onChange={(newPagination) => {
                    loadDecorations(newPagination.current, activeTab);
                }}
            />

            <UserBadgesModal
                userModalVisible={userModalVisible}
                setUserModalVisible={setUserModalVisible}
                selectedBadge={selectedBadge}
                setSelectedBadge={setSelectedBadge}
            />

            <Modal
                title={<div
                    style={{textAlign: "center"}}>{editingBadge ? 'Edit Decoration' : 'Create Decoration'}</div>}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={handleOk}
                destroyOnClose
            >
                <Form form={form} layout="vertical">

                    <Form.Item
                        name="image"
                        label="Image (256x256)"
                        style={{width: 200}}
                    >
                        <Upload
                            listType="picture"
                            maxCount={1}
                            fileList={fileList}
                            onRemove={() => {
                                setFileList([]);
                                form.setFieldsValue({icon: null});
                            }}
                            beforeUpload={(file) => {
                                const isImageOrJson = file.type.startsWith('image/') || file.type === 'application/json';
                                if (!isImageOrJson) {
                                    message.error('You can only upload image or Lottie JSON files!');
                                    return Upload.LIST_IGNORE;
                                }

                                const reader = new FileReader();
                                reader.onload = (e) => {
                                    const base64 = e.target.result;

                                    form.setFieldsValue({icon: base64});

                                    setFileList([
                                        {
                                            uid: '-1',
                                            name: file.name,
                                            status: 'done',
                                            url: base64,
                                            type: file.type,
                                        },
                                    ]);
                                };

                                reader.readAsDataURL(file);

                                return Upload.LIST_IGNORE;
                            }}
                            customRequest={({onSuccess}) => {
                                setTimeout(() => onSuccess("ok"), 0); // resolve fake upload
                            }}
                        >
                            {fileList.length === 0 && (
                                <Button icon={<UploadOutlined/>}>Upload Icon</Button>
                            )}
                        </Upload>
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label="Title"
                        rules={[{required: true, message: 'Please enter title'}]}
                    >
                        <Input/>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <Input/>
                    </Form.Item>

                    <Form.Item
                        name="type"
                        label="Type"
                        initialValue="badge"
                        rules={[{ required: true, message: "Select a type" }]}
                    >
                        <TypeSelector />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="progress_name"
                                label="Progress name"
                            >
                                <Input/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="total_count"
                                label="Progress required count"
                            >
                                <InputNumber style={{width: '100%'}}/>
                            </Form.Item>
                        </Col>
                    </Row>

                        <Form.Item
                            name="metadata"
                            label="Metadata (json)"
                        >
                            <Input.TextArea rows={4} placeholder='{"top": "-60%", "zIndex": 0}' />
                        </Form.Item>
                </Form>
            </Modal>
        </Content>
    );
};

const UserBadgesModal = ({userModalVisible, setUserModalVisible, selectedBadge}) => {
    const [userOptions, setUserOptions] = useState([]);
    const [assignedUsers, setAssignedUsers] = useState([]);


    useEffect(() => {
        if (selectedBadge) {
            Api.get(`/admin/decorations/${selectedBadge.id}`).then(res => {
                setAssignedUsers(res.data.data.users);
            })
        }
    }, [selectedBadge])

    const searchUsers = (query) => {
        Api.get(`/admin/user-list?page=1&search=${query}`).then(res => {
            const allUsers = res.data.data;
            console.log(allUsers);
            setUserOptions(allUsers)
        })
    };

    const addUserToBadge = (user) => {
        Api.post(`/admin/users/${user.user_id}/decorations`, {
            decoration_id: selectedBadge.id,
            is_visible: false,
        }).then(res => {
            setAssignedUsers([...assignedUsers, user]);
            message.success('User added successfully');
        })
    };

    const removeUserFromBadge = (userId) => {
        Api.delete(`/admin/users/${userId}/decorations/${selectedBadge.id}`).then(res => {
            setAssignedUsers(prev => prev.filter(user => user.user_id !== userId));
            message.success('User removed successfully');
        })
    };

    return (
        <Modal
            title={<div style={{textAlign: "center"}}>Assign Users to: <strong>{selectedBadge?.name}</strong></div>}
            open={userModalVisible}
            onCancel={() => setUserModalVisible(false)}
            footer={null}
            destroyOnClose
        >
            <Row type="flex" justify="center" alignItems="center">
                <Image style={{maxWidth: 100, margin: "auto", display: "block"}}
                       src={selectedBadge?.image_url.replace('v2.fitlap.ee', 'fitlap.in')}></Image>
            </Row>
            <br/>
            <Form layout="vertical">
                <Form.Item label={<strong>Search Users</strong>}>
                    <Select
                        showSearch
                        placeholder="Search users"
                        onSearch={searchUsers}
                        filterOption={false}
                        onSelect={(value) => {
                            const user = userOptions.find(u => u.user_id === value);

                            if (user) addUserToBadge(user);
                        }}
                    >
                        {userOptions.map(user => (
                            <Select.Option key={user.user_id} value={user.user_id}>
                                {user.user_nicename} <Tag>{user.email}</Tag>
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item label={<strong>Users with this badge ({assignedUsers.length})</strong>}>
                    <List
                        dataSource={assignedUsers}
                        locale={{emptyText: 'No users assigned yet.'}}
                        renderItem={user => (
                            <List.Item
                                actions={[
                                    <Button
                                        danger
                                        size="small"
                                        onClick={() => removeUserFromBadge(user.user_id)}
                                        key="remove"
                                    >
                                        Remove
                                    </Button>,
                                ]}
                            >
                                <div>
                                    <Avatar src={user.avatar} icon={<UserOutlined/>}/> {user.user_nicename}
                                </div>
                            </List.Item>
                        )}
                    />
                </Form.Item>

            </Form>
        </Modal>
    )
}

const TYPES = [
    {
        value: "badge",
        label: "Badge",
        description: "Achievement style icon below avatar",
    },
    {
        value: "sticker",
        label: "Sticker",
        description: "Overlay user avatar",
    },
    {
        value: "banner",
        label: "Banner",
        description: "Wide profile background",
    },
];

const TypeSelector = ({ value, onChange }) => {
    return (
        <Row gutter={12}>
            {TYPES.map((type) => {
                const active = value === type.value;

                return (
                    <Col span={8} key={type.value}>
                        <div
                            onClick={() => onChange(type.value)}
                            style={{
                                border: active ? "2px solid #1677ff" : "1px solid #ddd",
                                borderRadius: 12,
                                padding: 12,
                                cursor: "pointer",
                                textAlign: "center",
                                transition: "all 0.2s",
                                background: active ? "#f0f7ff" : "#fff",
                            }}
                        >

                            <div style={{ fontWeight: 600 }}>
                                {type.label}
                            </div>

                            <div style={{ fontSize: 12, color: "#888" }}>
                                {type.description}
                            </div>
                        </div>
                    </Col>
                );
            })}
        </Row>
    );
};

export default BadgesView;
